/**
 * QuizPage — quiz-module player (EPIC-08-02).
 *
 * Route: /app/quiz/:moduleId?enrollmentId=<id>
 *
 * Loads questions (correct answers stripped server-side), collects answers,
 * submits for grading, then shows the score + per-question feedback. A passing
 * submission recalculates enrollment progress server-side and unlocks the next
 * module on return to the course detail page.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/client';
import styles from './QuizPage.module.css';

interface QuizOption { text: string; }
interface QuizQuestion {
  id: string;
  sequence: number;
  question_text: string;
  question_type: string;
  options: QuizOption[] | null;
  points: number;
}
interface QuestionResult {
  id: string;
  question_text: string;
  user_answer: string;
  correct: boolean;
  correct_answer: string | null;
  explanation: string | null;
}
interface QuizResult {
  score: number;
  passed: boolean;
  questions: QuestionResult[];
}

export function QuizPage(): JSX.Element {
  const { moduleId } = useParams<{ moduleId: string }>();
  const [searchParams] = useSearchParams();
  const enrollmentId = searchParams.get('enrollmentId') ?? '';
  const { t } = useTranslation('training');
  const navigate = useNavigate();

  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [result, setResult] = useState<QuizResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!moduleId) return;
    let cancelled = false;

    async function load(): Promise<void> {
      try {
        const res = await apiClient.get<{ data: { questions: QuizQuestion[] } }>(
          `/quiz/${moduleId}/questions`,
        );
        if (!cancelled) {
          setQuestions([...res.data.data.questions].sort((a, b) => a.sequence - b.sequence));
        }
      } catch {
        if (!cancelled) setError('load_failed');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void load();
    return () => { cancelled = true; };
  }, [moduleId]);

  const allAnswered = questions.length > 0 && questions.every((q) => answers[q.id]);

  async function handleSubmit(): Promise<void> {
    if (!moduleId || !enrollmentId || submitting || !allAnswered) return;
    setSubmitting(true);
    try {
      const res = await apiClient.post<{ data: QuizResult }>('/quiz/submit', {
        enrollment_id: enrollmentId,
        module_id: moduleId,
        answers,
      });
      setResult(res.data.data);
    } catch {
      setError('submit_failed');
    } finally {
      setSubmitting(false);
    }
  }

  if (isLoading) {
    return (
      <main className={styles.page}>
        <p className={styles.loading}>{t('common.loading', 'Loading…')}</p>
      </main>
    );
  }

  if (error || questions.length === 0) {
    return (
      <main className={styles.page}>
        <button className={styles.backLink} onClick={() => navigate(-1)}>
          ← {t('common.back', 'Back')}
        </button>
        <p className={styles.error}>
          {t('quiz.loadError', 'Could not load this quiz. Please try again.')}
        </p>
      </main>
    );
  }

  // ── Results view ──────────────────────────────────────────────────────────
  if (result) {
    return (
      <main className={styles.page}>
        <div className={`${styles.resultBanner} ${result.passed ? styles.passed : styles.failed}`}>
          <span className={styles.resultScore}>{Math.round(result.score)}%</span>
          <span className={styles.resultLabel}>
            {result.passed
              ? t('quiz.passed', 'Passed')
              : t('quiz.notPassed', 'Not yet passed')}
          </span>
        </div>

        <ol className={styles.resultList}>
          {result.questions.map((q) => (
            <li
              key={q.id}
              className={`${styles.resultItem} ${q.correct ? styles.itemCorrect : styles.itemWrong}`}
            >
              <div className={styles.resultQuestion}>
                <span className={styles.resultIcon}>{q.correct ? '✅' : '❌'}</span>
                {q.question_text}
              </div>
              <p className={styles.resultAnswer}>
                {t('quiz.yourAnswer', 'Your answer')}: <strong>{q.user_answer}</strong>
              </p>
              {!q.correct && q.correct_answer && (
                <p className={styles.correctAnswer}>
                  {t('quiz.correctAnswer', 'Correct answer')}: <strong>{q.correct_answer}</strong>
                </p>
              )}
              {q.explanation && <p className={styles.explanation}>{q.explanation}</p>}
            </li>
          ))}
        </ol>

        <div className={styles.footer}>
          <button
            className={styles.primaryBtn}
            onClick={() => navigate(`/learn/${enrollmentId}`)}
          >
            {t('quiz.backToCourse', 'Back to course')}
          </button>
        </div>
      </main>
    );
  }

  // ── Question view ─────────────────────────────────────────────────────────
  return (
    <main className={styles.page}>
      <button className={styles.backLink} onClick={() => navigate(`/learn/${enrollmentId}`)}>
        ← {t('common.back', 'Back to course')}
      </button>

      <header className={styles.header}>
        <span className={styles.kicker}>📝 {t('quiz.kicker', 'Knowledge Check')}</span>
        <h1 className={styles.title}>{t('quiz.title', 'Quiz')}</h1>
        <p className={styles.subtitle}>
          {t('quiz.subtitle', 'Answer all questions, then submit to see your score.')}
        </p>
      </header>

      <ol className={styles.questionList}>
        {questions.map((q, idx) => (
          <li key={q.id} className={styles.questionCard}>
            <div className={styles.questionText}>
              <span className={styles.questionNum}>{idx + 1}</span>
              {q.question_text}
            </div>
            <div className={styles.options}>
              {(q.options ?? []).map((opt) => {
                const selected = answers[q.id] === opt.text;
                return (
                  <label
                    key={opt.text}
                    className={`${styles.option} ${selected ? styles.optionSelected : ''}`}
                  >
                    <input
                      type="radio"
                      name={q.id}
                      value={opt.text}
                      checked={selected}
                      onChange={() => setAnswers((a) => ({ ...a, [q.id]: opt.text }))}
                    />
                    <span>{opt.text}</span>
                  </label>
                );
              })}
            </div>
          </li>
        ))}
      </ol>

      <div className={styles.footer}>
        <button
          className={styles.primaryBtn}
          onClick={() => void handleSubmit()}
          disabled={!allAnswered || submitting}
        >
          {submitting
            ? t('quiz.submitting', 'Submitting…')
            : t('quiz.submit', 'Submit answers')}
        </button>
      </div>
    </main>
  );
}
